import { useState, useEffect } from "react";
import { apiFetch } from "../../api/api";
import { T } from "../data/Tokens";
import {
  Card, PageHeader, GoldBtn, GhostBtn, OutlineBtn, StatusPill,
  SearchBar, Th, Td, Tr, Tabs, StatCard, EmptyState,
  Field, Input, Select, SectionTitle, ConfirmModal
} from "../components/common/Ui";

const CATEGORIES = ["Daily Planner","Travel Planner","Wellness Journal","Gratitude Journal","Bullet Journal","Recipe Journal","Lined Notebook","Dotted Notebook","Photobook","Gift Hamper"];

const EMPTY_FORM = { id:"", name:"", category:"Daily Planner", stock:"", reorder:"10", incoming:"0", cost:"" };

const computeStatus = (stock, reorder) => {
  const s = parseInt(stock) || 0;
  const r = parseInt(reorder) || 10;
  if (s === 0) return "Out of Stock";
  if (s <= r)  return "Low Stock";
  return "Active";
};

// ─── Item Form Modal ───────────────────────────────────────────────────────────
function ItemFormModal({ item, onClose, onSave, saving }) {
  const [form, setForm] = useState(item
    ? { dbId:item.dbId, id:item.id, name:item.name, category:item.category, stock:String(item.stock), reorder:String(item.reorder), incoming:String(item.incoming), cost:item.cost }
    : { ...EMPTY_FORM }
  );
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const isEdit = !!item?.dbId;

  const handleSave = () => {
    if (!form.name.trim()) return alert("Product name is required!");
    if (!form.id.trim())   return alert("SKU is required!");
    if (form.stock === "") return alert("Stock quantity is required!");
    onSave({
      ...form,
      stock:    parseInt(form.stock)    || 0,
      reorder:  parseInt(form.reorder)  || 10,
      incoming: parseInt(form.incoming) || 0,
      status:   computeStatus(form.stock, form.reorder),
    });
  };

  const previewStatus = computeStatus(form.stock, form.reorder);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-0 sm:px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
      <div className="w-full h-full sm:h-auto sm:max-w-xl rounded-none sm:rounded-3xl overflow-hidden flex flex-col sm:max-h-[90vh]"
        style={{ background:T.card, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>

        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0" style={{ borderBottom:`1px solid ${T.border}` }}>
          <div className="min-w-0">
            <h2 className="text-[16px] sm:text-[18px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>
              {isEdit ? "Edit Inventory Item" : "Add New Item"}
            </h2>
            <p className="text-[11px] truncate" style={{ color:T.textSec }}>
              {isEdit ? `Editing: ${item.name}` : "Add a new product to inventory"}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-2xl leading-none flex-shrink-0 ml-2">×</button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-4 sm:p-6 space-y-4 sm:space-y-5">

          {/* Product Info */}
          <div className="rounded-2xl p-4" style={{ background:T.bgSoft }}>
            <SectionTitle>Product Info</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="SKU / Product ID" required>
                <Input value={form.id} onChange={e => set("id", e.target.value.toUpperCase())} placeholder="AC-DP-001" />
              </Field>
              <Field label="Category">
                <Select value={form.category} onChange={e => set("category", e.target.value)}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </Select>
              </Field>
              <div className="sm:col-span-2">
                <Field label="Product Name" required>
                  <Input value={form.name} onChange={e => set("name", e.target.value)} placeholder="Getting Things Done" />
                </Field>
              </div>
              <Field label="Cost Price (₹)">
                <Input value={form.cost} onChange={e => set("cost", e.target.value)} placeholder="620" type="number" />
              </Field>
            </div>
          </div>

          {/* Stock Info */}
          <div className="rounded-2xl p-4" style={{ background:T.bgSoft }}>
            <SectionTitle>Stock Details</SectionTitle>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Field label="Current Stock" required>
                <Input value={form.stock} onChange={e => set("stock", e.target.value)} placeholder="48" type="number" />
              </Field>
              <Field label="Reorder Level">
                <Input value={form.reorder} onChange={e => set("reorder", e.target.value)} placeholder="10" type="number" />
              </Field>
              <Field label="Incoming Units">
                <Input value={form.incoming} onChange={e => set("incoming", e.target.value)} placeholder="0" type="number" />
              </Field>
            </div>
            <p className="text-[10px] mt-2" style={{ color: T.textSec }}>
              Note: Incoming units are not saved to the server yet — for display only.
            </p>
          </div>

          {/* Live preview */}
          {form.name && (
            <div className="rounded-2xl p-4 flex items-center gap-4 flex-wrap sm:flex-nowrap" style={{ background:T.goldL, border:`1px solid ${T.champagne}` }}>
              <div className="min-w-0">
                <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color:T.goldD }}>Preview</p>
                <p className="text-[13px] font-bold truncate" style={{ color:T.textPri }}>{form.name}</p>
                <p className="text-[11px] truncate" style={{ color:T.textSec }}>{form.id || "No SKU"} · {form.category}</p>
              </div>
              <div className="sm:ml-auto text-right flex-shrink-0">
                <p className="text-[22px] font-bold"
                  style={{ color: parseInt(form.stock)===0 ? T.danger : parseInt(form.stock)<=parseInt(form.reorder) ? T.warning : T.success }}>
                  {form.stock || "0"}
                </p>
                <p className="text-[10px]" style={{ color:T.textSec }}>units</p>
                <div className="mt-1">
                  {previewStatus === "Active"       && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-green-100 text-green-700">Active</span>}
                  {previewStatus === "Low Stock"    && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Low Stock</span>}
                  {previewStatus === "Out of Stock" && <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-red-100 text-red-600">Out of Stock</span>}
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between gap-2 px-4 sm:px-6 py-3.5 sm:py-4 flex-shrink-0" style={{ borderTop:`1px solid ${T.border}` }}>
          <button onClick={onClose} disabled={saving}
            className="px-4 py-2 rounded-xl text-[13px] font-medium border disabled:opacity-50"
            style={{ borderColor:T.border, color:T.textSec }}>Cancel</button>
          <button onClick={handleSave} disabled={saving}
            className="px-5 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:shadow-md active:scale-95 disabled:opacity-60"
            style={{ background:T.gold }}>
            {saving ? "Saving..." : (isEdit ? "✓ Update Item" : "✓ Add Item")}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Restock Modal ─────────────────────────────────────────────────────────────
function RestockModal({ item, onClose, onRestock, saving }) {
  const [qty, setQty] = useState("");
  const [note, setNote] = useState("");

  const handle = () => {
    if (!qty || parseInt(qty) <= 0) return alert("Enter valid quantity!");
    onRestock(item.dbId, parseInt(qty), note);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4" style={{ background:"rgba(0,0,0,0.5)" }}>
      <div className="w-full max-w-sm rounded-3xl overflow-hidden max-h-[90vh] overflow-y-auto" style={{ background:T.card, boxShadow:"0 20px 60px rgba(0,0,0,0.2)" }}>
        <div className="flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4" style={{ borderBottom:`1px solid ${T.border}` }}>
          <div className="min-w-0">
            <h2 className="text-[16px] font-bold" style={{ color:T.textPri, fontFamily:T.serif }}>Restock Item</h2>
            <p className="text-[11px] truncate" style={{ color:T.textSec }}>{item.name}</p>
          </div>
          <button onClick={onClose} className="text-gray-400 text-xl flex-shrink-0 ml-2">×</button>
        </div>
        <div className="p-5 sm:p-6 space-y-4">
          {/* Current */}
          <div className="flex items-center justify-between rounded-2xl p-3" style={{ background:T.bgSoft }}>
            <span className="text-[12px]" style={{ color:T.textSec }}>Current Stock</span>
            <span className="text-[15px] font-bold" style={{ color: item.stock===0?T.danger:T.warning }}>{item.stock} units</span>
          </div>
          {item.incoming > 0 && (
            <div className="flex items-center justify-between rounded-2xl p-3" style={{ background:"#EEF3FF" }}>
              <span className="text-[12px]" style={{ color:"#5B7EE5" }}>Already Incoming</span>
              <span className="text-[14px] font-bold" style={{ color:"#5B7EE5" }}>+{item.incoming} units</span>
            </div>
          )}
          <Field label="Add Stock Quantity">
            <Input value={qty} onChange={e => setQty(e.target.value)} placeholder="Enter units to add" type="number" />
          </Field>
          {qty && (
            <div className="flex items-center justify-between rounded-2xl p-3" style={{ background:"#EEFAF0" }}>
              <span className="text-[12px]" style={{ color:T.success }}>New Total Stock</span>
              <span className="text-[15px] font-bold" style={{ color:T.success }}>{item.stock + (parseInt(qty)||0)} units</span>
            </div>
          )}
          <Field label="Note (optional)">
            <input value={note} onChange={e => setNote(e.target.value)} placeholder="e.g. Received from supplier"
              className="w-full px-3 py-2.5 rounded-xl text-[13px] outline-none"
              style={{ background:T.bgSoft, border:`1px solid ${T.border}`, color:T.textPri }} />
          </Field>
          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <button onClick={handle} disabled={saving}
              className="flex-1 py-2.5 rounded-xl text-[13px] font-semibold text-white transition-all hover:shadow-md active:scale-95 disabled:opacity-60"
              style={{ background:T.success }}>
              {saving ? "Saving..." : "✓ Confirm Restock"}
            </button>
            <button onClick={onClose} disabled={saving}
              className="px-4 py-2.5 rounded-xl text-[13px] border font-medium disabled:opacity-50"
              style={{ borderColor:T.border, color:T.textSec }}>Cancel</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN
// ══════════════════════════════════════════════════════════════════════════════
export default function InventoryAdmin() {
  const [items, setItems]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving]     = useState(false);

  const [tab, setTab]           = useState("all");
  const [search, setSearch]     = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [restockItem, setRestockItem] = useState(null);
  const [delId, setDelId]       = useState(null);
  const [toast, setToast]       = useState(null);

  const showToast = (msg, color = T.gold) => {
    setToast({ msg, color });
    setTimeout(() => setToast(null), 2500);
  };

  // ── Fetch inventory from backend ────────────────────────────────────────────
  const fetchItems = async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await apiFetch("/inventory/list.php");
      setItems(res.data || []);
    } catch (err) {
      setLoadError(err.message || "Failed to load inventory.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchItems(); }, []);

  // ── CRUD (backend connected) ────────────────────────────────────────────────
  const handleSave = async (formData) => {
    setSaving(true);
    try {
      if (formData.dbId) {
        await apiFetch("/inventory/update.php", {
          method: "PUT",
          body: {
            dbId: formData.dbId,
            id: formData.id,
            name: formData.name,
            category: formData.category,
            stock: formData.stock,
            reorder: formData.reorder,
            cost: formData.cost,
          },
        });
        showToast("✓ Item updated successfully", T.success);
      } else {
        await apiFetch("/inventory/create.php", {
          method: "POST",
          body: {
            id: formData.id,
            name: formData.name,
            category: formData.category,
            stock: formData.stock,
            reorder: formData.reorder,
            cost: formData.cost,
          },
        });
        showToast("✓ Item added to inventory", T.success);
      }
      await fetchItems();
      setShowForm(false); setEditItem(null);
    } catch (err) {
      alert(err.message || "Something went wrong while saving the item.");
    } finally {
      setSaving(false);
    }
  };

  const handleRestock = async (dbId, qty, note) => {
    setSaving(true);
    try {
      await apiFetch("/inventory/restock.php", {
        method: "POST",
        body: { dbId, qty, note },
      });
      await fetchItems();
      showToast(`✓ Restocked +${qty} units`, T.success);
      setRestockItem(null);
    } catch (err) {
      alert(err.message || "Failed to restock item.");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (dbId) => {
    try {
      await apiFetch("/inventory/delete.php", {
        method: "DELETE",
        body: { dbId },
      });
      await fetchItems();
      showToast("🗑 Item removed from inventory", T.danger);
    } catch (err) {
      alert(err.message || "Failed to delete item.");
    } finally {
      setDelId(null);
    }
  };

  const handleRestockAll = async () => {
    const lowItems = items.filter(i => i.status === "Low Stock" || i.status === "Out of Stock");
    if (!lowItems.length) return showToast("No low stock items to restock", T.textSec);
    if (!confirm(`Restock ${lowItems.length} low/out-of-stock item(s)?`)) return;
    try {
      for (const item of lowItems) {
        const addQty = item.reorder * 3;
        await apiFetch("/inventory/restock.php", {
          method: "POST",
          body: { dbId: item.dbId, qty: addQty, note: "Bulk restock" },
        });
      }
      await fetchItems();
      showToast("✓ All low stock items restocked", T.success);
    } catch (err) {
      alert(err.message || "Failed to restock some items.");
    }
  };

  // ── Live stats ─────────────────────────────────────────────────────────────
  const totalValue = items.reduce((a, i) => a + (parseFloat(i.cost)||0) * i.stock, 0);
  const STATS = [
    { icon:"📦", label:"Total SKUs",      value: items.length,                                    sub:"In inventory",           color:T.gold },
    { icon:"✅", label:"In Stock",        value: items.filter(i=>i.status==="Active").length,      sub:"Sufficient stock",        color:T.success },
    { icon:"⚠️", label:"Low Stock",       value: items.filter(i=>i.status==="Low Stock").length,   sub:"Below reorder level",    color:T.warning, bg:"#FFF8EC" },
    { icon:"❌", label:"Out of Stock",    value: items.filter(i=>i.status==="Out of Stock").length,sub:"Needs immediate restock", color:T.danger,  bg:"#FFF0F0" },
    { icon:"🚚", label:"Incoming",        value: items.filter(i=>i.incoming>0).length,             sub:"In transit",             color:"#5B7EE5", bg:"#EEF3FF" },
    { icon:"💰", label:"Stock Value",     value:`₹${(totalValue/1000).toFixed(0)}K`,               sub:"At cost price",          color:T.gold },
  ];

  const TABS = [
    { id:"all",          label:"All",          count: items.length },
    { id:"active",       label:"In Stock",     count: items.filter(i=>i.status==="Active").length },
    { id:"low-stock",    label:"Low Stock",    count: items.filter(i=>i.status==="Low Stock").length },
    { id:"out-of-stock", label:"Out of Stock", count: items.filter(i=>i.status==="Out of Stock").length },
    { id:"incoming",     label:"Incoming",     count: items.filter(i=>i.incoming>0).length },
  ];

  const filtered = items.filter(item => {
    const ms = !search || item.name.toLowerCase().includes(search.toLowerCase()) || item.id.toLowerCase().includes(search.toLowerCase());
    const mt = tab === "all"
      || (tab === "active"       && item.status === "Active")
      || (tab === "low-stock"    && item.status === "Low Stock")
      || (tab === "out-of-stock" && item.status === "Out of Stock")
      || (tab === "incoming"     && item.incoming > 0);
    return ms && mt;
  });

  return (
    <div>
      <PageHeader title="Inventory" subtitle="Track stock levels and manage restocking">
        <GhostBtn onClick={() => {}}>↓ Export CSV</GhostBtn>
        <GhostBtn onClick={handleRestockAll}>⚡ Restock All Low</GhostBtn>
        <GoldBtn onClick={() => { setEditItem(null); setShowForm(true); }}>+ Add Item</GoldBtn>
      </PageHeader>

      {/* Loading / Error states */}
      {loading && (
        <div className="flex items-center justify-center py-16">
          <div className="flex flex-col items-center gap-2">
            <div className="w-8 h-8 rounded-full animate-spin"
              style={{ border: `3px solid ${T.border}`, borderTopColor: T.gold }} />
            <p className="text-[12px]" style={{ color: T.textSec }}>Loading inventory...</p>
          </div>
        </div>
      )}

      {!loading && loadError && (
        <Card className="p-6 text-center mb-5">
          <p className="text-[13px] font-semibold mb-2" style={{ color: T.danger }}>{loadError}</p>
          <button onClick={fetchItems}
            className="px-4 py-2 rounded-xl text-[12px] font-semibold text-white"
            style={{ background: T.gold }}>
            Retry
          </button>
        </Card>
      )}

      {!loading && !loadError && (
        <>
          {/* Stats */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2.5 sm:gap-3 mb-5 sm:mb-6">
            {STATS.map(s => <StatCard key={s.label} {...s} />)}
          </div>

          <Tabs tabs={TABS} active={tab} onChange={setTab} />

          <Card>
            <div className="px-4 sm:px-5 py-3.5 sm:py-4 flex flex-col sm:flex-row gap-2.5 sm:gap-3 items-stretch sm:items-center" style={{ borderBottom:`1px solid ${T.border}` }}>
              <SearchBar value={search} onChange={setSearch} placeholder="Search by SKU or product name..." />
              <select className="w-full sm:w-auto text-[12px] px-3 py-2.5 rounded-xl outline-none flex-shrink-0"
                style={{ border:`1px solid ${T.border}`, background:"#fff", color:T.textSec }}>
                <option>All Categories</option>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>

            {filtered.length === 0
              ? <EmptyState icon="📦" title="No inventory items found" sub="Add your first item or change filters."
                  action="Add Item" onAction={() => { setEditItem(null); setShowForm(true); }} />
              : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[900px]">
                    <thead>
                      <tr style={{ borderBottom:`1px solid ${T.border}` }}>
                        <Th>SKU</Th><Th>Product</Th><Th>Category</Th>
                        <Th>Stock</Th><Th>Reorder At</Th><Th>Incoming</Th>
                        <Th>Cost</Th><Th>Status</Th><Th>Actions</Th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((item, i) => (
                        <Tr key={item.dbId} last={i === filtered.length - 1}>
                          <Td>
                            <span className="text-[11px] font-mono font-semibold px-2 py-1 rounded-lg"
                              style={{ background:T.bgSoft, color:T.textSec }}>{item.id}</span>
                          </Td>
                          <Td>
                            <p className="text-[12px] font-semibold" style={{ color:T.textPri }}>{item.name}</p>
                          </Td>
                          <Td>
                            <span className="text-[11px]" style={{ color:T.textSec }}>{item.category}</span>
                          </Td>
                          <Td>
                            {/* Stock bar */}
                            <div>
                              <span className="text-[14px] font-bold"
                                style={{ color: item.stock===0?T.danger:item.stock<=item.reorder?T.warning:T.success }}>
                                {item.stock}
                              </span>
                              <div className="w-16 h-1.5 rounded-full mt-1" style={{ background:T.border }}>
                                <div className="h-full rounded-full transition-all"
                                  style={{ width:`${Math.min(100,(item.stock/((item.reorder||1)*5))*100)}%`,
                                    background: item.stock===0?T.danger:item.stock<=item.reorder?T.warning:T.success }} />
                              </div>
                            </div>
                          </Td>
                          <Td>
                            <span className="text-[12px]" style={{ color:T.textSec }}>{item.reorder}</span>
                          </Td>
                          <Td>
                            {item.incoming > 0
                              ? <span className="text-[12px] font-semibold px-2 py-0.5 rounded-full"
                                  style={{ background:"#EEF3FF", color:"#5B7EE5" }}>+{item.incoming}</span>
                              : <span className="text-[12px]" style={{ color:T.textSec }}>—</span>}
                          </Td>
                          <Td>
                            <span className="text-[12px] font-semibold" style={{ color:T.textPri }}>₹{item.cost}</span>
                          </Td>
                          <Td><StatusPill s={item.status} /></Td>
                          <Td>
                            <div className="flex gap-1.5 flex-wrap">
                              {/* Edit */}
                              <button
                                onClick={() => { setEditItem(item); setShowForm(true); }}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:bg-amber-50 active:scale-95"
                                style={{ borderColor:T.gold, color:T.gold }}>
                                ✏️ Edit
                              </button>
                              {/* Restock */}
                              {(item.status === "Low Stock" || item.status === "Out of Stock") && (
                                <button
                                  onClick={() => setRestockItem(item)}
                                  className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold text-white transition-all hover:shadow-md active:scale-95"
                                  style={{ background:T.success }}>
                                  📦 Restock
                                </button>
                              )}
                              {/* Delete */}
                              <button
                                onClick={() => setDelId(item.dbId)}
                                className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all hover:bg-red-50 active:scale-95"
                                style={{ borderColor:T.danger, color:T.danger }}>
                                🗑
                              </button>
                            </div>
                          </Td>
                        </Tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

            {/* Footer summary */}
            <div className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row gap-2 sm:gap-0 items-start sm:items-center justify-between" style={{ borderTop:`1px solid ${T.border}` }}>
              <p className="text-[11px]" style={{ color:T.textSec }}>
                Showing {filtered.length} of {items.length} items
              </p>
              <div className="flex items-center gap-3 sm:gap-4 text-[10px] sm:text-[11px] flex-wrap">
                <span style={{ color:T.danger }}>❌ Out of stock: {items.filter(i=>i.status==="Out of Stock").length}</span>
                <span style={{ color:T.warning }}>⚠️ Low stock: {items.filter(i=>i.status==="Low Stock").length}</span>
                <span style={{ color:T.success }}>✅ Healthy: {items.filter(i=>i.status==="Active").length}</span>
              </div>
            </div>
          </Card>
        </>
      )}

      {/* ── Add / Edit Modal ── */}
      {showForm && (
        <ItemFormModal
          item={editItem}
          saving={saving}
          onClose={() => { if (!saving) { setShowForm(false); setEditItem(null); } }}
          onSave={handleSave}
        />
      )}

      {/* ── Restock Modal ── */}
      {restockItem && (
        <RestockModal
          item={restockItem}
          saving={saving}
          onClose={() => { if (!saving) setRestockItem(null); }}
          onRestock={handleRestock}
        />
      )}

      {/* ── Delete Confirm ── */}
      {delId && (
        <ConfirmModal
          title="Delete Item?"
          message={`"${items.find(i=>i.dbId===delId)?.name}" will be permanently removed from inventory.`}
          onConfirm={() => handleDelete(delId)}
          onCancel={() => setDelId(null)}
        />
      )}

      {/* ── Toast ── */}
      {toast && (
        <div className="fixed bottom-4 left-4 right-4 sm:left-auto sm:right-6 sm:bottom-6 z-[100] px-5 py-3 rounded-2xl text-white text-[13px] font-semibold shadow-2xl text-center sm:text-left"
          style={{ background:toast.color }}>
          {toast.msg}
        </div>
      )}
    </div>
  );
}